import React, { useState, useCallback, useRef } from 'react';
import { apiClient } from '../clients/APIClient';

export interface UploadProgress {
  isUploading: boolean;
  currentFile: string;
  currentIndex: number;
  totalFiles: number;
  percentage: number;
  isCancelling: boolean;
}

interface CompletedFileInfo {
  id: string;
  filename: string;
  bytes: number;
}

// Create a progress handler for file uploads
const _createProgressHandler = (
  uploadCancelledRef: React.MutableRefObject<boolean>,
  completedFilesRef: React.MutableRefObject<Map<string, CompletedFileInfo>>,
  setUploadProgress: React.Dispatch<React.SetStateAction<UploadProgress>>,
  onProgress?: (progress: UploadProgress) => void,
  onError?: (error: string, filename: string) => void,
  filename?: string
) => {
  return (event: any, data: any) => {
    if (uploadCancelledRef.current) {
      return;
    }

    if (data.error) {
      console.error('Upload status changed.');
      setUploadProgress(prev => ({
        ...prev,
        isUploading: false,
        isCancelling: false
      }));
      if (onError && filename) {
        onError(data.error, filename);
      }
    } else if (data.chunk) {
      const progressUpdates = data.chunk.split('data: ').filter((item: string) => item.trim().length > 0);

      progressUpdates.forEach((update: string) => {
        if (update.trim() === '[DONE]') {
          return;
        }
        try {
          const progressData = JSON.parse(update);
          if (progressData && typeof progressData.progress === 'number') {
            const progressPercentage = Math.round(progressData.progress * 100);

            // Store completed file info when upload finishes
            if (progressData.progress === 1 && progressData.id && progressData.filename) {
              completedFilesRef.current.set(progressData.filename, {
                id: progressData.id,
                filename: progressData.filename,
                bytes: progressData.bytes || 0
              });
            }

            // Update progress
            setUploadProgress(prev => {
              if (!prev.isCancelling) {
                const newProgress = {
                  ...prev,
                  percentage: progressPercentage
                };
                onProgress?.(newProgress);
                return newProgress;
              }
              return prev;
            });
          }
        } catch (e) {
          console.error('Upload status changed.');
        }
      });
    }
  };
};

export interface FileDetails {
  name: string;
  path?: string;  // Desktop only: file system path
  file?: File;    // Web only: File object from input/drag-drop
}

export interface UploadOptions {
  collectionId?: string;
  purpose?: string;
  onProgress?: (progress: UploadProgress) => void;
  onSuccess?: (fileId: string, filename: string) => void;
  onError?: (error: string, filename: string) => void;
  onComplete?: (successCount: number, errorCount: number) => void;
}

export interface UseFileUploadReturn {
  uploadProgress: UploadProgress;
  uploadFiles: (files: FileDetails[], options?: UploadOptions) => Promise<void>;
  uploadBase64File: (base64Content: string, fileName: string, options?: UploadOptions) => Promise<void>;
  cancelUpload: () => Promise<void>;
  isUploading: boolean;
  resetProgress: () => void;
}

export const useFileUpload = (): UseFileUploadReturn => {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    isUploading: false,
    currentFile: '',
    currentIndex: 0,
    totalFiles: 0,
    percentage: 0,
    isCancelling: false
  });

  const uploadCancelledRef = useRef(false);
  const completedFilesRef = useRef<Map<string, { id: string; filename: string; bytes: number }>>(new Map());

  // Read File object as base64 (for web browsers)
  const readFileAsBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Extract base64 content (remove data URL prefix if present)
        const base64Content = result.includes('base64,') 
          ? result.split('base64,')[1] 
          : result;
        resolve(base64Content);
      };
      reader.onerror = () => reject(new Error(`Failed to read file(web mode): ${file.name}`));
      reader.readAsDataURL(file);
    });
  }, []);

  // Reset progress state
  const resetProgress = useCallback(() => {
    setUploadProgress({
      isUploading: false,
      currentFile: '',
      currentIndex: 0,
      totalFiles: 0,
      percentage: 0,
      isCancelling: false
    });
    uploadCancelledRef.current = false;
    completedFilesRef.current.clear();
  }, []);

  // Cancel upload
  const cancelUpload = useCallback(async () => {
    try {
      setUploadProgress(prev => ({
        ...prev,
        isCancelling: true
      }));

      uploadCancelledRef.current = true;

      // TODO: MIGRATION - Migrated from window.electron.openAIHandler.stopFileUpload to apiClient
      await apiClient.files.stopFileUpload();
      
      // Reset progress state
      setUploadProgress({
        isUploading: false,
        currentFile: '',
        currentIndex: 0,
        totalFiles: 0,
        percentage: 0,
        isCancelling: false
      });
      completedFilesRef.current.clear();

    } catch (error) {
      console.error('Upload status changed.');
      uploadCancelledRef.current = false;
      setUploadProgress(prev => ({
        ...prev,
        isCancelling: false
      }));
    }
  }, []);

  // Main upload function - supports both web (File objects) and desktop (file paths)
  const uploadFiles = useCallback(async (files: FileDetails[], options: UploadOptions = {}) => {
    if (files.length === 0) return;

    const {
      collectionId,
      purpose = 'assistants',
      onProgress,
      onSuccess,
      onError,
      onComplete
    } = options;

    // Set up progress event listener for HTTP SSE stream
    const handleFileUploadProgress = (event: Event): void => {
      const customEvent = event as CustomEvent;
      if (uploadCancelledRef.current) {
        console.log('Upload status changed.');
        return;
      }
      
      const progressData = customEvent.detail;
      if (progressData && typeof progressData.progress === 'number') {
        const progressPercentage = Math.round(progressData.progress * 100);
        console.log('Upload status changed.');
        
        // Store completed file info when upload finishes
        if (progressData.progress === 1 && progressData.id && progressData.filename) {
          console.log('Upload status changed.');
          completedFilesRef.current.set(progressData.filename, {
            id: progressData.id,
            filename: progressData.filename,
            bytes: progressData.bytes || 0
          });
        }
        
        // Update progress
        setUploadProgress(prev => {
          if (!prev.isCancelling) {
            const newProgress = {
              ...prev,
              percentage: progressPercentage
            };
            onProgress?.(newProgress);
            return newProgress;
          }
          return prev;
        });
      }
    };

    try {
      const totalFiles = files.length;
      let successCount = 0;
      let errorCount = 0;
      let currentIndex = 0;

      // Reset cancellation flag
      uploadCancelledRef.current = false;

      // Initialize upload progress
      const initialProgress: UploadProgress = {
        isUploading: true,
        currentFile: '',
        currentIndex: 0,
        totalFiles,
        percentage: 0,
        isCancelling: false
      };
      setUploadProgress(initialProgress);
      onProgress?.(initialProgress);

      // Set up progress event listener for HTTP SSE stream
      // Progress comes from CustomEvent dispatched by processFileUploadStream in apiClient
      window.addEventListener('file-upload-progress', handleFileUploadProgress);

      // Process each file sequentially
      for (const fileDetails of files) {
        currentIndex += 1;
        const capturedIndex = currentIndex;
        
        // Check if upload was cancelled
        if (uploadCancelledRef.current) {
          break;
        }
        
        // Update progress with current file
        setUploadProgress(prev => {
          const newProgress = {
            ...prev,
            currentFile: fileDetails.name,
            currentIndex: capturedIndex
          };
          onProgress?.(newProgress);
          return newProgress;
        });
        
        try {
          // Read file content based on platform
          let fileContent: string;
          
          if (fileDetails.file) {
            // Web mode: Use FileReader API to read File object
            fileContent = await readFileAsBase64(fileDetails.file);
          } else if (fileDetails.path && window.electron?.readFile) {
            // Desktop mode: Use Electron's file system access
            console.log('Upload status changed.');
            const result = await window.electron.readFile(fileDetails.path);
            if (typeof result === 'object' && 'error' in result) {
              onError?.(result.error, fileDetails.name);
              errorCount += 1;
              continue;
            }
            fileContent = result;
          } else {
            onError?.('Invalid file details', fileDetails.name);
            errorCount += 1;
            continue;
          }
          
          // Check cancellation after file read
          if (uploadCancelledRef.current) {
            break;
          }
          
          let fileId: string | null = null;
          
          const uploadRequest = {
            file: fileContent,
            title: fileDetails.name,
            purpose
          };
          
          // Clear any previous file info for this filename
          completedFilesRef.current.delete(fileDetails.name);
          
          console.log('Upload status changed.');
          // A collection upload must enter through the collection endpoint.
          // Controlled collections retain the encrypted original at ingestion;
          // uploading to the general library first would irreversibly discard it.
          const uploadResult = collectionId && collectionId !== '-1'
            ? await apiClient.collections.addFileToCollection(collectionId, uploadRequest)
            : await apiClient.files.addFile(uploadRequest);
          
          
          // Check cancellation after main upload
          if (uploadCancelledRef.current) {
            break;
          }

          if (!uploadResult || !uploadResult.id) {
            onError?.('No file info received from server', fileDetails.name);
            errorCount += 1;
            continue;
          }

          fileId = uploadResult.id;
          onSuccess?.(fileId, uploadResult.filename);
          
          // Count as success only after the destination upload completes.
          successCount += 1;
          
        } catch (error) {
          console.error('Upload status changed.');
          onError?.(error instanceof Error ? error.message : 'Unknown error', fileDetails.name);
          errorCount += 1;
        }
      }
      
      // Reset upload progress
      resetProgress();
      
      // Only call onComplete if not cancelled
      if (!uploadCancelledRef.current) {
        onComplete?.(successCount, errorCount);
      }
      
    } catch (error) {
      console.error('Upload status changed.');
      resetProgress();
    } finally {
      // Remove event listener for file upload progress
      window.removeEventListener('file-upload-progress', handleFileUploadProgress);
    }
  }, [resetProgress, readFileAsBase64]);

  // Upload a single base64 file (for ChatInput)
  const uploadBase64File = useCallback(async (base64Content: string, fileName: string, options: UploadOptions = {}) => {

    const {
      collectionId = '-1', // Default to "All" collection for ChatInput
      purpose = 'assistants',
      onProgress,
      onSuccess,
      onError,
      onComplete
    } = options;

    // Set up progress event listener for HTTP SSE stream
    // Progress comes from CustomEvent dispatched by processFileUploadStream in apiClient
    const handleProgress = (event: Event): void => {
      const customEvent = event as CustomEvent;
      const progressData = customEvent.detail;
      if (progressData && typeof progressData.progress === 'number') {
        const progressPercentage = Math.round(progressData.progress * 100);
        
        // Store completed file info when upload finishes
        if (progressData.progress === 1 && progressData.id && progressData.filename) {
          completedFilesRef.current.set(progressData.filename, {
            id: progressData.id,
            filename: progressData.filename,
            bytes: progressData.bytes || 0
          });
        }
        
        // Update progress
        setUploadProgress(prev => {
          if (!prev.isCancelling) {
            const newProgress = {
              ...prev,
              percentage: progressPercentage
            };
            onProgress?.(newProgress);
            return newProgress;
          }
          return prev;
        });
      }
    };

    try {
      // Reset cancellation flag at the start of a new base64 upload
      uploadCancelledRef.current = false;

      // Initialize upload progress
      const initialProgress: UploadProgress = {
        isUploading: true,
        currentFile: fileName,
        currentIndex: 1,
        totalFiles: 1,
        percentage: 0,
        isCancelling: false
      };
      setUploadProgress(initialProgress);
      onProgress?.(initialProgress);

      window.addEventListener('file-upload-progress', handleProgress);
      
      const fileAddRequest = {
        file: base64Content,
        title: fileName,
        purpose
      };
      
      // Clear any previous file info for this filename
      completedFilesRef.current.delete(fileName);
      
      const completedFileInfo = collectionId && collectionId !== '-1'
        ? await apiClient.collections.addFileToCollection(collectionId, fileAddRequest)
        : await apiClient.files.addFile(fileAddRequest);
      if (uploadCancelledRef.current) return;

      if (!completedFileInfo) {
        console.error('Upload status changed.');
        onError?.('No file info received from server', fileName);
        return;
      }
      
      onSuccess?.(completedFileInfo.id, completedFileInfo.filename);
      
      // Reset upload progress
      resetProgress();
      
      // Call onComplete with success
      if (!uploadCancelledRef.current) {
        onComplete?.(1, 0);
      }
      
    } catch (error) {
      console.error('Upload status changed.');
      onError?.(error instanceof Error ? error.message : 'Unknown error', fileName);
      resetProgress();
    } finally {
      // Clean up event listener
      window.removeEventListener('file-upload-progress', handleProgress);
    }
  }, [resetProgress]);

  return {
    uploadProgress,
    uploadFiles,
    uploadBase64File,
    cancelUpload,
    isUploading: uploadProgress.isUploading,
    resetProgress
  };
}; 
